import { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import ActivityIndicator from '../components/activity-indicator'
import { useQuery } from '../hooks/use-query'
import Navigation from '../components/main/navigation/navigation'
import { mutation, query } from '../graphql/api'
import { useAnalyseData } from '../hooks/use-analyse-data'
import Questionaire from '../components/main/questionaire'
import Maintenance from '../components/maintenance'
import { classNames, getDimensions } from '../utils/helpers'
import { newScoreCalc } from '../utils/pnorm'
import { localStorage } from '../utils/local-storage'
import Footer from '../components/footer'
import LanguageSwitch from '../components/language-switch'
import { trackEvent } from '../utils/analytics'
import { AnalyseContextProvider } from '../context/analyse-context'
import { useIsMobile } from '../hooks/use-is-mobile'
import { useMount } from '../hooks/use-mount'
import { sendSlackMessage } from '../utils/slack'
import { notifyBugsnag } from '../utils/bugsnag'

const Main = () => {
  const invitation = useQuery('invitation')?.replace(/\s/g, '')
  const location = useLocation()

  const { isMobile } = useIsMobile()

  const [partner, setPartner] = useState(null)
  const [career, setCareer] = useState(null)
  const [careerAnalyse, setCareerAnalyse] = useState(null)

  const [state, setState] = useState('INITIAL')
  const [error, setError] = useState(null)
  const [navigateToState, setNavigateToState] = useState(null)

  const { isLoading, data, setSeen, updateEstimation } = useAnalyseData({
    analyse: careerAnalyse
  })

  const finishAnalyse = useCallback(async () => {
    const dimensions = [
      ...data.PERSONALITY.dimensions,
      ...data.SKILLS.dimensions,
      ...data.INTERESTS.dimensions
    ]

    const userInput = Object.fromEntries(
      dimensions.map(({ key, estimation }) => {
        const score = newScoreCalc(estimation)
        return [key, { score }]
      })
    )

    if (
      // check if all dimensions are set
      Object.keys(userInput).length !== dimensions.length ||
      dimensions.some(({ estimation }) => estimation === -1)
    ) {
      // if not -> clear input and start from scratch
      setError('ESTIMATION_UNSET_ERROR')

      mutation({
        mutation: 'updateCareerAnalyse',
        input: {
          id: invitation,
          // null is not possible, because it's a delete operation
          input: JSON.stringify({}),
          time_needed: JSON.stringify({})
        },
        authMode: 'AWS_IAM'
      })

      const message = ['ESTIMATION_UNSET_ERROR', JSON.stringify(userInput)]
      notifyBugsnag(new Error(message.join(' / ')))

      return
    }

    // copy same requirement
    // PROBLEM_SOLVING_score to ANALOGICAL_REASONING_score
    userInput.ANALOGICAL_REASONING_score = JSON.parse(
      JSON.stringify(userInput.PROBLEM_SOLVING_score)
    )

    const ca = await query({
      query: 'getCareerAnalyse',
      variables: { id: invitation },
      authMode: 'AWS_IAM'
    })

    const time_needed = JSON.parse(ca.time_needed)
    const totalSeconds = Object.values(time_needed).reduce(
      (sum, value) => sum + value,
      0
    )

    const total_time_needed_in_min = Math.round(totalSeconds / 60)

    const input = {
      id: invitation,
      url_params: JSON.stringify(location),
      verified: true,
      status: 'COMPLETED',
      partner_id: partner.id,
      career_id: career.id,
      total_time_needed_in_min,
      input: JSON.stringify(userInput)
    }

    if (userInput._temp !== undefined) {
      setError('ERROR_UNHANDLED')
      notifyBugsnag(new Error('Invalid user input with _temp field: ' + JSON.stringify(userInput)))
      return
    }

    // TODO: CHECK COMPLETED STATUS

    try {
      await mutation({
      mutation: 'updateCareerAnalyse',
      input,
      authMode: 'AWS_IAM'
      })

      const message = `${partner.name} hat soeben eine Anforderungsanalyse zum Profil ${career.title} abgeschlossen.`
      sendSlackMessage({ message })
      trackEvent('analyse_finished', { state: 'OUTRO' })
    } catch (err) {
      notifyBugsnag(err)
    }

    setState('OUTRO')
  }, [career, data, invitation, location, partner])

  const context = useMemo(
    () => ({
      state,
      setState,
      setError,
      navigateToState,
      setNavigateToState,
      invitation,
      partner,
      career,
      data,
      setSeen,
      updateEstimation,
      finishAnalyse,
      careerAnalyse
    }),
    [
      state,
      setState,
      setError,
      navigateToState,
      setNavigateToState,
      invitation,
      partner,
      career,
      data,
      setSeen,
      updateEstimation,
      finishAnalyse,
      careerAnalyse
    ]
  )

  useMount(() => {
    if (!invitation) {
      setError('ERROR_NO_INVITATION')
      return
    }

    const initialize = async () => {
      const ca = await query({
        query: 'getCareerAnalyse',
        variables: { id: invitation },
        authMode: 'AWS_IAM'
      })

      if (!ca) {
        setError('CAREER_ANALYSE_NOT_FOUND')
        return
      }

      const { id: career_analysis_id, partner_id, career_id } = ca

      localStorage.setItem('career-analysis-id', career_analysis_id)
      localStorage.setItem('partner-id', partner_id)
      localStorage.setItem('career-id', career_id)

      trackEvent('launch')

      const [p, c] = await Promise.all([
        query({
          query: 'getPartner',
          variables: { id: partner_id },
          authMode: 'AWS_IAM'
        }),
        query({
          query: 'getCareer',
          variables: { id: career_id },
          authMode: 'AWS_IAM'
        })
      ])

      if (!c) {
        setError('CAREER_NOT_FOUND')
        return
      }

      setPartner(p)
      setCareer(c)
      setCareerAnalyse(ca)

      localStorage.setItem('partner-name', p.name)

      if (ca.status === 'COMPLETED') {
        setState('OUTRO')
        return
      }

      const getLastState = () => {
        const { seen } = JSON.parse(ca.input || '{}')._temp || {}

        if (!seen) return undefined
        return seen[seen.length - 1]
      }

      const lastState = getLastState()
      if (lastState) {
        setState(lastState)
        trackEvent('use_temp_input', { dimension: lastState })
      } else {
        setState('INTRO_0')
      }
    }

    initialize()
  })

  if (
    [
      'ERROR_NO_INVITATION',
      'ERROR_GET_DATA',
      'CAREER_ANALYSE_NOT_FOUND',
      'CAREER_NOT_FOUND',
      'ESTIMATION_UNSET_ERROR'
    ].includes(error)
  ) {
    return <Maintenance state={error} />
  }

  if (state === 'INITIAL' || isLoading) {
    return (
      <div className='flex justify-center pt-64'>
        <ActivityIndicator />
      </div>
    )
  }

  if (isMobile) {
    const dimensions = getDimensions({ data })
    const analyseKeys = Array.from(dimensions.keys())

    return (
      <AnalyseContextProvider value={context}>
        <div
          className={classNames(
            'absolute h-10 right-4 top-0 pl-3 bg-white z-10',
            state.includes('INTRO') ? 'flex justify-end items-center' : 'hidden'
          )}
        >
          <LanguageSwitch />
        </div>
        <div className='flex flex-col h-screen'>
          <div className='mt-4'>
            <Navigation />
          </div>
          <div
            style={{
              ...(analyseKeys.includes(state) && {
                height: '100vh',
                minHeight: 640,
                overflow: 'hidden'
              })
            }}
          >
            <Questionaire />
          </div>
          {analyseKeys.includes(state) ? null : (
            <div className='mt-auto w-full px-8 py-3'>
              <Footer />
            </div>
          )}
        </div>
      </AnalyseContextProvider>
    )
  }

  return (
    <AnalyseContextProvider value={context}>
      <div className='fixed h-10 right-4 top-0 pl-3 flex justify-end items-center bg-white z-10'>
        <LanguageSwitch />
      </div>
      <div
        style={{
          width: 260,
          height: '100vh',
          minHeight: 768,
          float: 'left',
          overflow: 'auto'
        }}
      >
        <Navigation />
      </div>
      <div
        style={{
          height: '100vh',
          minHeight: 768,
          overflow: state === 'OVERVIEW' ? 'auto' : 'hidden'
        }}
      >
        <Questionaire />
      </div>
      <div className='fixed h-10 left-0 bottom-0 w-full px-4 flex justify-end items-center bg-white'>
        <Footer />
      </div>
    </AnalyseContextProvider>
  )
}

export default Main
